import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui-compat'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  Send,
  Search,
  Mail,
  CheckCircle,
  XCircle,
  Trash2,
  X,
  LoaderCircle,
  Lock,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { transmittalStatusVariant, type StatusVariant } from '@/lib/status-variants'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { formatDate } from '../../../utils/formatters'
import { useUrlParam } from '../../../hooks'
import QuickTransmittalModal from './QuickTransmittalModal'
import Grid from '../../../components/grid'

interface Transmittal {
  id: string
  name: string
  description: string | null
  reference: string | null
  confidential: boolean
  status: 'DRAFT' | 'SENT' | 'VIEWED' | 'EXPIRED'
  sentAt: string | null
  expiresAt: string | null
  token: string
  url: string
  isExpired: boolean
  itemCount: number
  recipientCount: number
  createdAt: string
  createdBy: {
    id: string
    name: string
  }
}

const getStatusIcon = (status: string, isExpired: boolean) => {
  if (isExpired) return XCircle
  switch (status) {
    case 'SENT':
      return Mail
    case 'VIEWED':
      return CheckCircle
    case 'EXPIRED':
      return XCircle
    default:
      return Mail
  }
}

const getStatusVariant = (status: string, isExpired: boolean): StatusVariant =>
  isExpired ? 'danger' : transmittalStatusVariant(status)

const TransmittalsList: React.FC = () => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [transmittalIdParam] = useUrlParam('transmittalId')
  const [selectedTransmittalId, setSelectedTransmittalId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false)

  // Auto-open transmittal when navigated from activity log
  useEffect(() => {
    if (transmittalIdParam && !selectedTransmittalId) {
      setSelectedTransmittalId(transmittalIdParam)
    }
  }, [transmittalIdParam])

  // Current project — transmittals are project-scoped
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id

  const { canDelete, canApprove } = useModuleActions('DOC_CONTROL', projectId)

  // Fetch transmittals for the current project, excluding drafts
  const { data, loading, refetch } = useQuery(Query.DOC_TRANSMITTALS, {
    variables: {
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      projectId,
    },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  })

  const [deleteTransmittal] = useMutation(Mutation.DELETE_DOC_TRANSMITTAL, {
    onCompleted: () => {
      toastr.success('', t('DocControl_transmittal_deleted'))
      refetch()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  const [bulkDeleteTransmittals, { loading: bulkDeleting }] = useMutation(
    Mutation.BULK_DELETE_DOC_TRANSMITTALS,
    {
      onCompleted: (data) => {
        const deletedCount = data?.bulkDeleteDocTransmittals?.length || 0
        toastr.success('', t('DocControl_transmittals_bulk_deleted', { count: deletedCount }))
        setSelectedIds([])
        refetch()
      },
      onError: (error) => {
        toastr.error(t('Common_error'), error.message)
      },
    }
  )

  const transmittals: Transmittal[] = (data?.docTransmittals || []).filter(
    (t: Transmittal) => t.status !== 'DRAFT'
  )

  // Filter by search
  const filteredTransmittals = useMemo(() => {
    return transmittals.filter((t) => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return (
        t.name.toLowerCase().includes(query) ||
        t.reference?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      )
    })
  }, [transmittals, searchQuery])

  const handleCreate = () => {
    setCreateModalOpen(true)
  }

  const handleOpenTransmittal = (id: string) => {
    setSelectedTransmittalId(id)
  }

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    toastr.success('', t('DocControl_link_copied'))
  }

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(t('DocControl_transmittal_delete_confirm', { name }))) {
      deleteTransmittal({ variables: { id } })
    }
  }

  const handleCreateModalClose = (updated?: boolean) => {
    setCreateModalOpen(false)
    if (updated) {
      refetch()
    }
  }

  const handleTransmittalModalClose = (updated?: boolean) => {
    setSelectedTransmittalId(null)
    if (updated) {
      refetch()
    }
  }

  const handleClearSelection = () => setSelectedIds([])

  const handleBulkDelete = () => {
    if (selectedIds.length > 0) setDeleteConfirmModalOpen(true)
  }

  const handleConfirmBulkDelete = async () => {
    await bulkDeleteTransmittals({ variables: { ids: selectedIds } })
    setDeleteConfirmModalOpen(false)
  }

  // Grid columns
  const columns = [
    { name: 'name', title: t('DocControl_transmittal_name') },
    { name: 'reference', title: t('DocControl_reference'), width: 120 },
    { name: 'status', title: t('DocControl_status'), width: 100 },
    { name: 'itemCount', title: t('DocControl_documents'), width: 80 },
    { name: 'recipientCount', title: t('DocControl_recipients'), width: 80 },
    { name: 'sentAt', title: t('DocControl_sent_at'), width: 120 },
  ]

  // Grid formatters
  const formatters = [
    {
      formatter: ({ row }: { row: Transmittal }) => (
        <div>
          <div className="font-medium flex items-center gap-1.5">
            {row.name}
            {row.confidential && (
              <Lock
                className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                aria-label={t('DocControl_confidential', 'Confidential')}
              />
            )}
          </div>
          {row.description && (
            <small className="text-muted-foreground block truncate max-w-[250px]">
              {row.description}
            </small>
          )}
        </div>
      ),
      for: ['name'],
    },
    {
      formatter: ({ row }: { row: Transmittal }) =>
        row.reference || <span className="text-muted-foreground">-</span>,
      for: ['reference'],
    },
    {
      formatter: ({ row }: { row: Transmittal }) => {
        const StatusIcon = getStatusIcon(row.status, row.isExpired)
        return (
          <Badge
            variant={getStatusVariant(row.status, row.isExpired)}
            className="font-medium whitespace-nowrap"
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {row.isExpired
              ? t('DocControl_status_expired')
              : t(`DocControl_status_${row.status.toLowerCase()}`)}
          </Badge>
        )
      },
      for: ['status'],
    },
    {
      formatter: ({ row }: { row: Transmittal }) => (
        <span className="text-sm font-medium">{row.itemCount}</span>
      ),
      for: ['itemCount'],
    },
    {
      formatter: ({ row }: { row: Transmittal }) => (
        <span className="text-sm font-medium">{row.recipientCount}</span>
      ),
      for: ['recipientCount'],
    },
    {
      formatter: ({ row }: { row: Transmittal }) =>
        row.sentAt ? (
          <div>
            <div>{formatDate(row.sentAt)}</div>
            {row.expiresAt && (
              <small className={row.isExpired ? 'text-destructive' : 'text-muted-foreground'}>
                {row.isExpired
                  ? t('DocControl_expired')
                  : t('DocControl_expires', { date: formatDate(row.expiresAt) })}
              </small>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
      for: ['sentAt'],
    },
  ]

  // Row actions
  const rowActions = [
    {
      title: t('Common_view'),
      action: (row: Transmittal) => handleOpenTransmittal(row.id),
    },
    {
      title: t('DocControl_copy_link'),
      show: (row: Transmittal) => !!row.url,
      action: (row: Transmittal) => handleCopyLink(row.url),
    },
    canDelete && {
      title: t('Common_delete'),
      color: 'danger',
      divider: true,
      action: (row: Transmittal) => handleDelete(row.id, row.name),
    },
  ].filter(Boolean)

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {/* Left: action buttons.
            "Send transmittal" opens QuickTransmittalModal in create mode, whose only
            submit is createAndSendDocTransmittal → DOC_CONTROL.APPROVE per registry. */}
        {canApprove && (
          <Button size="sm" className="h-8" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            {t('DocControl_send_transmittal')}
          </Button>
        )}

        {/* Selection actions */}
        {selectedIds.length > 0 && (
          <>
            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                className="h-8"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                <Trash2 className="h-4 w-4" />
                {t('Common_delete')}
              </Button>
            )}
            <Badge variant="secondary" className="font-medium">
              {t('DocControl_transmittals_selected_count', { count: selectedIds.length })}
            </Badge>
            <Button variant="ghost" size="sm" className="text-muted-foreground h-7 text-xs" onClick={handleClearSelection}>
              <X className="h-3.5 w-3.5" />
              {t('Common_clear_selection')}
            </Button>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1 min-w-0" />

        {/* Right: filters + search */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-auto min-w-[150px] gap-2 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('DocControl_all_status')}</SelectItem>
            <SelectItem value="SENT">{t('DocControl_status_sent')}</SelectItem>
            <SelectItem value="VIEWED">{t('DocControl_status_viewed')}</SelectItem>
            <SelectItem value="EXPIRED">{t('DocControl_status_expired')}</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('Common_search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-[200px] pl-8 text-sm"
          />
        </div>
      </div>

      {/* Content */}
      <div className="min-h-[200px]">
        {!meData || (loading && !data) ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredTransmittals.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground py-12">
            <Send className="h-12 w-12 mb-3 text-muted-foreground/50" />
            <p className="mb-0">{t('DocControl_no_transmittals')}</p>
          </div>
        ) : (
          <Grid
            columns={columns}
            rows={filteredTransmittals}
            formatters={formatters}
            rowActions={rowActions}
            onRowClick={(row: Transmittal) => handleOpenTransmittal(row.id)}
            getRowId={(row: Transmittal) => row.id}
            showHeader
            selection={selectedIds}
            onSelectionChange={setSelectedIds}
            showSelectionColumn
          />
        )}
      </div>

      {/* Create Modal */}
      {createModalOpen && (
        <QuickTransmittalModal isOpen={createModalOpen} onClose={handleCreateModalClose} />
      )}

      {/* View/Edit Modal */}
      {selectedTransmittalId && (
        <QuickTransmittalModal
          isOpen={!!selectedTransmittalId}
          onClose={handleTransmittalModalClose}
          transmittalId={selectedTransmittalId}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModalOpen} toggle={() => setDeleteConfirmModalOpen(false)} centered>
        <ModalHeader toggle={() => setDeleteConfirmModalOpen(false)}>
          <Trash2 className="h-4 w-4 mr-2 text-destructive inline" />
          {t('DocControl_transmittals_delete_confirm_title')}
        </ModalHeader>
        <ModalBody>
          {t('DocControl_transmittals_bulk_delete_confirm', { count: selectedIds.length })}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteConfirmModalOpen(false)}>
            {t('Common_cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirmBulkDelete} disabled={bulkDeleting}>
            <Trash2 className="h-4 w-4" />
            {t('Common_delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default TransmittalsList
